import React, { useEffect, useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Card, CardBody, Table, MenuSVG, HeaderTitle } from '../../../../components';
import { FilterQueryParams } from '../../../../helpers';
import { usePaymentMovementsUIContext } from '../context/PaymentMovementsUIContext';
import PaymentMovementsFilters from './PaymentMovementsFilters';
import page from '../../../../constants/pages/page';
import { reportsService, usersService } from '../../../../services';
import { UIHelpers } from '../../../../constants/pages/UIHelpers';
import paymentMovementsColumns from './PaymentMovementsColumns';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { downloadCSV } from '../../../../helpers/download';
import axios from 'axios';
import moment from 'moment';
import { checkFileStatus } from '../../../../helpers/fibonacciHelper';
import CustomAlert from '../../../../components/Alert';

function PaymentMovementsTable () {
    const { t } = useTranslation();
    const paymentLinksFiltersRef = useRef();
    const customSortedKeyMap = { 'status.description': 'id_payment_link_status' };
    const { sortedKeyMap, defaultSorted } = UIHelpers(customSortedKeyMap);
    // State

    // Context State
    const paymentMovementsUIContext = usePaymentMovementsUIContext();
    const paymentMovementsUIProps = useMemo(() => {
        return {
            isLoading: paymentMovementsUIContext.isLoading,
            setIsLoading: paymentMovementsUIContext.setIsLoading,
            queryParams: paymentMovementsUIContext.queryParams,
            setQueryParams: paymentMovementsUIContext.setQueryParams,
            paymentMovements: paymentMovementsUIContext.paymentMovements,
            setPaymentMovements: paymentMovementsUIContext.setPaymentMovements,
        };
    }, [paymentMovementsUIContext]);

    // Alert states
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });
    const [openAlert, setOpenAlert] = useState(false);
    const [totalPayments, setTotalPayments] = useState(0);

    // Dialogs state
    const [dialog, setDialog] = useState();
    const [anchorEl] = useState(null);

    // Functions
    const fetchPayments = async () => {
        // change state isLoading
        paymentMovementsUIProps.setIsLoading(true);
        // prepare options (order, pages and filters)
        const filter = paymentMovementsUIProps.queryParams.filters || new FilterQueryParams();
        const { sortField, sortOrder } = paymentMovementsUIProps.queryParams;
        const options = {
            order: `${sortedKeyMap[sortField] || sortField}-${sortOrder.toUpperCase()}`,
            pageSize: paymentMovementsUIProps.queryParams.pageSize,
            page: paymentMovementsUIProps.queryParams.pageNumber,
        };

        // Set filters on options.
        options.filters = filter.build();

        try {
            const paymentsLinks = await usersService.getPaymentMovements(options);
            paymentMovementsUIProps.setPaymentMovements(paymentsLinks);
            paymentMovementsUIProps.setIsLoading(false);
        }
        catch (error) {
            paymentMovementsUIProps.setPaymentMovements([]);
            setAlert((prevAlert) => ({
                ...prevAlert,
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'clientsMC_Alert--error'
            }));
            setOpenAlert(true);
            paymentMovementsUIProps.setIsLoading(false);
        }
    };

    const getTotalLinks = async () => {
        paymentMovementsUIProps.setIsLoading(true);
        try {
            const filters = paymentMovementsUIProps.queryParams.filters || new FilterQueryParams();
            const options = {
                unionPeople: true,
                pageSize: 100000
            };
            options.filters = filters.build();
            const paymentsLinksTotal = await usersService.getPaymentMovements(options);
            setTotalPayments(paymentsLinksTotal.length);
            paymentMovementsUIProps.setIsLoading(false);
        }
        catch (error) {
            paymentMovementsUIProps.setIsLoading(false);
            setAlert((prevAlert) => ({
                ...prevAlert,
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'clientsMC_Alert--error'
            }));
            setOpenAlert(true);
        }
    };

    const getData = async () => {
        await getTotalLinks();
        fetchPayments();
    };

    // Handle query params changes. On any param change, fetch users.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => paymentMovementsUIProps.setQueryParams(prev => ({ ...prev, pageSize: localStorage.getItem('sizePerPage') || page.pageSize })), []);
    useEffect(() => {
        if (JSON.stringify(paymentMovementsUIProps.queryParams.url) !== undefined) {
            getData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentMovementsUIProps.queryParams]);

    const resetFilters = () => {
        if (paymentLinksFiltersRef.current) {
            paymentLinksFiltersRef.current.reset();
        }
    };

    const getDownloadFilters = () => {
        if (!JSON.stringify(paymentMovementsUIProps?.queryParams?.filters?.filters)) return '';
        const filters = { ...paymentMovementsUIProps?.queryParams?.filters?.filters };
        delete filters.created_at;
        return JSON.stringify(filters)
            .replace(/[{}"]/g, '')
            .replace(/:/g, ' ')
            .replace(replaceCommaPattern, replaceComma);
    };

    const replaceCommaPattern = /\[([^,]+),([^,\]]+)\]/;
    const replaceComma = (match, p1, p2) => {
        return `[${p1};${p2}]`;
    };

    const handleDownload = async () => {
        paymentMovementsUIProps.setIsLoading(true);

        const fetchFile = (idFile) => {
            return checkFileStatus({ filters: `id eq ${idFile}` }, 60, 3);
        };
        const startDate = paymentMovementsUIProps.queryParams?.filters?.filters?.created_at?.be[0] || moment().subtract(730, 'days').format('YYYY-MM-DD HH:mm:ss');
        const endDate = paymentMovementsUIProps.queryParams?.filters?.filters?.created_at?.be[1] || moment().format('YYYY-MM-DD HH:mm:ss');
        const filters = (paymentMovementsUIProps.queryParams?.filters?.filters?.created_at?.be[0] ? (`created_at be [${startDate};${endDate}]`) : '') + ((paymentMovementsUIProps.queryParams?.filters?.filters?.created_at?.be[0] && getDownloadFilters()) ? `,${getDownloadFilters()}` : getDownloadFilters() || '');
        const options = {
            idType: 31, // id report of payment movements table
            startAt: startDate || '2023-08-21 06:00:00',
            endAt: endDate,
            metadata: {
                filters: filters,
                order: 'id-DESC',
            }
        };
        const nameTable = window.location.pathname.slice(1, window.location.pathname.length);
        const fileName = startDate ? `Reporte del ${options.startAt} al ${options.endAt} ${nameTable}.csv` : `Reporte ${nameTable}.csv`;
        await reportsService
            .createReport(options)
            .then(async (response) => {
                return await fetchFile(response.metadata.id_file);
            })
            .then(async (res) => {
                return await axios.get(res[0].description, { responseType: 'arraybuffer' });
            })
            .then((response) => {
                downloadCSV(response.data, fileName);
            })
            .catch(() => {
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.downloadFile'),
                    className: 'clientsMC_Alert--error',
                }));
                setOpenAlert(true);
            })
            .finally(() => {
                paymentMovementsUIProps.setIsLoading(false);
            });
    };

    const columns = paymentMovementsColumns({
        dialog,
        setDialog,
        anchorEl
    });

    // Main Component
    return (
        <>
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />
            <Card>
                <Box>
                    <HeaderTitle
                        title={t('paymentMovements.table.title')}
                        total={totalPayments}
                        reset={resetFilters}
                        icon={(<MenuSVG className='mr-1 mb-2'/>)}
                        UIProps={paymentMovementsUIProps}
                        margBottom={'6px'}
                        download={handleDownload}
                    />
                    <PaymentMovementsFilters ref={paymentLinksFiltersRef} />
                </Box>
                <CardBody>
                    <Table
                        data={paymentMovementsUIProps.paymentMovements}
                        columns={columns}
                        sort={defaultSorted}
                        onChange={paymentMovementsUIProps.setQueryParams}
                        isLoading={paymentMovementsUIProps.isLoading}
                        total={totalPayments}
                        pagination={{
                            sizePerPage: paymentMovementsUIProps.queryParams.pageSize,
                            page: paymentMovementsUIProps.queryParams.pageNumber
                        }}
                    />
                </CardBody>
            </Card>
        </>
    );
};

const mapStateToProps = ({ auth: { user } }) => ({
    user
});

export default connect(mapStateToProps)(PaymentMovementsTable);
